import type { Price } from '@misumi/shared'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import type {
  PartConfiguratorFieldError,
  PartConfiguratorFormState,
} from '../parts/components/part-configurator-save'
import type {
  PartConfiguratorCapabilities,
  PartConfiguratorSubject,
} from '../parts/components/part-configurator-subject'
import { deriveLineReadiness } from './cart-readiness'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type CartLineSpecification = components['schemas']['CartLineSpecification']
type CartLineSpecificationInput = components['schemas']['CartLineSpecificationInput']
type CartViewWithLineSpecifications = components['schemas']['CartViewWithLineSpecifications']
type QuantityUom = components['schemas']['Quantity']['uom']

/**
 * UID-434/WS-E — Cart's own capability set for the shared
 * `PartConfiguratorShell` (`part-configurator-subject.ts`). Per the human D3
 * ruling for this ticket, every tab stays VISIBLE for Cart — `dfmCapable:
 * false` and `attachmentsCapable: false` render honest empty states on the
 * Manufacturability-feedback / Drawing-analysis tabs rather than hiding
 * them, and `applyToMultiple: false` means the multi-select action never
 * renders at all for a Cart line (that's a Parts-Library-only concept).
 *
 * `colorCapable`/`threadedHolesCapable`/`additionalRequirementsCapable` are
 * all `true`: `CartLineSpecificationInput` (`apps/api/openapi/schemas/
 * cart.yaml`) mirrors `EditPartConfigRequest`'s field typing verbatim for
 * these three, so nothing the shell can render for them would be silently
 * discarded on save. `customInspectionCapable` stays `false` — that's an
 * RFQ-only routing field (`RfqLineSpecification.customInspection`);
 * `CartLineSpecificationInput` has no field to save a `true` value into, so
 * Cart must never offer the control (the exact UID-403 bug 3 shape this
 * ticket exists to not reintroduce).
 */
export const CART_CONFIGURATOR_CAPABILITIES: PartConfiguratorCapabilities = {
  applyToMultiple: false,
  dfmCapable: false,
  attachmentsCapable: false,
  colorCapable: true,
  threadedHolesCapable: true,
  additionalRequirementsCapable: true,
  customInspectionCapable: false,
  // UID-434 human ruling (post-launch copy fix) — a Cart line lands in the
  // shell's `'rfq'` footer state because a spec edit reset its price, not
  // because the configuration needs a manual quote. See
  // `PartConfiguratorCapabilities.rfqFooterCopyKind`'s own doc comment.
  rfqFooterCopyKind: 'cartLineReset',
}

export interface FromCartLineParams {
  readonly line: LineItem
  readonly presentation: CartLinePresentation | undefined
  /** The line's PERSISTED manufacturing configuration (UID-434 WS-C,
   * `CartViewWithLineSpecifications.lineSpecifications`), or `undefined`
   * when this line has never been configuration-edited — the two are kept
   * distinct all the way through (never defaulted to an empty object),
   * exactly as the migration's nullable jsonb column and the API's own
   * `CartLineSpecificationEntry` doc comment require. */
  readonly specification: CartLineSpecification | undefined
  readonly displayName: string
}

/**
 * UID-434/WS-E — the Cart adapter onto the vertical-agnostic
 * `PartConfiguratorSubject` (see that type's own doc, `part-configurator-
 * subject.ts`, for the shape's full rationale). Mirrors `fromLibraryPartRow`
 * in spirit — a pure, lossless-for-the-shell projection — but Cart has no
 * `LibraryPartRow` to project: it builds `configAttributes` from the
 * line's real persisted `CartLineSpecification` instead of a
 * `Sourceable.configAttributes` bag, and never fabricates `dfmSummary`,
 * `pricingProjection`, `sceneKey`, or `attachmentsPartId` — none of those
 * are concepts a Cart line carries (see `CART_CONFIGURATOR_CAPABILITIES`).
 *
 * `indicativePrice` is the line's own REAL `Price` when the line is
 * currently priced, and is genuinely absent (never a stale/fabricated
 * value) once a spec-changing save has reset it — see
 * `buildCartLineSpecificationPatch`'s own doc for how that reset is kept
 * honest end-to-end.
 *
 * `filterStatus: 'quote'` is set only for a line the cart's own readiness
 * derivation (`deriveLineReadiness`, the SAME function the row's "Quote
 * required" chip reads) already calls quote-required — this is what makes
 * the shell's built-in pricing footer render its `rfq` state (with a "Save
 * and close" CTA) for those lines, instead of the `notReady` state's plain
 * info callout, which renders NO close-affordance at all. Every other line
 * carries no `rowStatus`/`filterStatus`/`pricingProjection` at all: Cart
 * has no `auto_priced`-style projection concept, so the shell's footer can
 * only ever land on `notReady` or `rfq` for a Cart subject — `priced`
 * (whose CTA is "Add to cart · $X", a nonsensical action for a line already
 * IN the cart) is simply unreachable here, by construction, not by a
 * special case.
 */
export function fromCartLine({
  line,
  presentation,
  specification,
  displayName,
}: FromCartLineParams): PartConfiguratorSubject {
  const configAttributes: Record<string, unknown> = {}
  if (specification) {
    if (specification.process !== undefined) configAttributes.process = specification.process
    if (specification.material !== undefined) configAttributes.material = specification.material
    // `finish: null` is a real, persisted answer ("as machined") — carried
    // through even though it's falsy, distinct from the key being absent.
    if ('finish' in specification) configAttributes.finish = specification.finish
    if (specification.color !== undefined) configAttributes.color = specification.color
    if (specification.threadedHoleCount !== undefined) {
      configAttributes.threadedHoleCount = specification.threadedHoleCount
    }
    if (specification.inspections !== undefined) {
      configAttributes.inspections = specification.inspections
    }
    if (specification.certificates !== undefined) {
      configAttributes.certificates = specification.certificates
    }
    if (specification.additionalRequirements !== undefined) {
      configAttributes.additionalRequirements = specification.additionalRequirements
    }
  }

  const readiness = deriveLineReadiness(line, presentation)

  return {
    id: line.sourceable.id,
    displayName,
    thumbnailUrl: null,
    drawingFilename: null,
    configAttributes: Object.keys(configAttributes).length > 0 ? configAttributes : undefined,
    quantity: line.quantity.value,
    ...(line.price ? { indicativePrice: line.price as Price } : {}),
    ...(readiness === 'quote-required' ? { filterStatus: 'quote' as const } : {}),
  }
}

/** Order-insensitive comparison (ADR-0117 code-review W3, mirrored here for
 * the FE side of the same rule `@misumi/shared`'s
 * `cartLineSpecificationsEqual` enforces server-side): reordering a
 * selection is not a configuration change. `null`/`undefined`/`[]` are all
 * treated as "nothing selected" for this comparison — the diff below only
 * ever WRITES a real empty-list clear (`null`) when the stored side had a
 * non-empty list, never a spurious no-op patch for two different flavors of
 * "nothing". */
function sameStringSet(
  a: readonly string[] | null | undefined,
  b: readonly string[] | null | undefined,
): boolean {
  const left = a ?? []
  const right = b ?? []
  if (left.length !== right.length) return false
  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()
  return sortedLeft.every((value, index) => value === sortedRight[index])
}

/**
 * UID-434/WS-E — diffs the shell's current form state against the line's
 * STORED specification and returns ONLY the changed keys as a
 * `CartLineSpecificationInput` partial edit, or `undefined` when there is
 * no real configuration change to persist.
 *
 * This is NOT optional polish — it is what makes two hard contract
 * requirements both true at once:
 *
 * 1. **A pure quantity change must never touch `specification` at all**
 *    ("route it through the existing cart quantity path", not the spec
 *    object). `PartConfiguratorFormState.process` is NON-NULLABLE on the
 *    shell's form — `buildFormState()` always sends SOME process, even for
 *    a subject the buyer never opened the Configuration tab on. Naively
 *    forwarding the whole form as `specification` on every save would (a)
 *    send a `process` the buyer never chose, and (b) — because
 *    `mergeCartLineSpecification` requires `material` alongside ANY
 *    non-empty result — turn a harmless quantity bump on a never-configured
 *    line into a guaranteed 400 ("material is required"). Diffing against
 *    the STORED spec (not the form's own defaults) means an untouched
 *    Configuration tab produces an empty diff, so `specification` is
 *    omitted and the PATCH carries `quantity` alone.
 *
 * 2. **`finish: null` must be sendable as the real "as machined" answer**,
 *    not conflated with "not yet chosen" (ADR-0117 D4; ~26 catalog
 *    materials offer no secondary finish at all, and `null` is the only
 *    valid response for them). The shell's own state can't tell those two
 *    apart — both render as `finish === null` on the form. This diff
 *    resolves the ambiguity the only way that is ever correct: whenever
 *    `material` is part of this edit (newly chosen OR changed), `finish`
 *    RIDES ALONG unconditionally, whatever the form currently holds — a
 *    material change invalidates any prior finish choice anyway, so
 *    resending it is never a stale value, and it's the only way a
 *    zero-finish material can ever be saved (a diff-based omission would
 *    otherwise never include `finish`, and `parseSpecification` rejects a
 *    merged spec with no `finish` key at all as "not yet answered").
 *
 * `tolerance` is never touched here — the shared shell exposes no control
 * for it (`PartConfiguratorFormState` has no `tolerance` field), so an
 * existing stored value is left alone by simply never appearing in the
 * diff (absence = unchanged, per the contract's own merge semantics).
 */
export function buildCartLineSpecificationPatch(
  form: PartConfiguratorFormState,
  stored: CartLineSpecification | undefined,
): CartLineSpecificationInput | undefined {
  const patch: Record<string, unknown> = {}

  if (form.material !== (stored?.material ?? null)) {
    patch.material = form.material
  }
  if (patch.material !== undefined || form.finish !== (stored?.finish ?? null)) {
    patch.finish = form.finish
  }
  if (form.color !== (stored?.color ?? null)) {
    patch.color = form.color
  }
  if (form.threadedHoleCount !== (stored?.threadedHoleCount ?? null)) {
    patch.threadedHoleCount = form.threadedHoleCount
  }
  if (!sameStringSet(form.inspections, stored?.inspections)) {
    patch.inspections =
      form.inspections && form.inspections.length > 0 ? [...form.inspections] : null
  }
  if (!sameStringSet(form.certificates, stored?.certificates)) {
    patch.certificates =
      form.certificates && form.certificates.length > 0 ? [...form.certificates] : null
  }
  const formAdditional = form.additionalRequirements?.trim() ? form.additionalRequirements : null
  if (formAdditional !== (stored?.additionalRequirements ?? null)) {
    patch.additionalRequirements = formAdditional
  }

  const explicitProcessChange = stored !== undefined && form.process !== stored.process
  if (explicitProcessChange || Object.keys(patch).length > 0) {
    patch.process = form.process
  }

  if (Object.keys(patch).length === 0) return undefined
  return patch as CartLineSpecificationInput
}

export interface CartLineConfiguratorPatchArgs {
  readonly sourceableId: string
  readonly quantity?: { readonly value: number; readonly uom: QuantityUom }
  readonly specification?: CartLineSpecificationInput
}

export type CartLineConfiguratorPatchOutcome =
  | { readonly ok: true; readonly cart: CartViewWithLineSpecifications }
  | {
      readonly ok: false
      readonly kind: 'field_errors'
      readonly errors: readonly PartConfiguratorFieldError[]
    }
  | { readonly ok: false; readonly kind: 'error'; readonly message: string }

export interface CartLineConfiguratorService {
  patch(args: CartLineConfiguratorPatchArgs): Promise<CartLineConfiguratorPatchOutcome>
}

/** The 400 Problem+JSON body carries an `errors` extension member the
 * generated `Problem` schema doesn't type (mirrors `EditPartConfigFieldError`'s
 * own doc comment in `edit-part-config-service.ts` — the Parts vertical's
 * identical situation for the SAME shell). Read defensively at this one call
 * site rather than widening the generated `Problem` type. */
interface CartLineConfigInvalidProblem {
  readonly errors?: readonly PartConfiguratorFieldError[]
}

async function bffPatchCartLineConfiguration(
  args: CartLineConfiguratorPatchArgs,
): Promise<CartLineConfiguratorPatchOutcome> {
  const { data, error, response } = await apiClient.PATCH('/cart/lines/{sourceableId}', {
    params: { path: { sourceableId: args.sourceableId } },
    body: {
      ...(args.quantity ? { quantity: args.quantity } : {}),
      ...(args.specification ? { specification: args.specification } : {}),
    },
  })

  if (response.status === 400) {
    const body = error as CartLineConfigInvalidProblem | undefined
    return { ok: false, kind: 'field_errors', errors: body?.errors ?? [] }
  }
  if (error || !data) {
    return {
      ok: false,
      kind: 'error',
      message: error?.detail ?? "We couldn't save this configuration. Please try again.",
    }
  }
  return { ok: true, cart: data }
}

export const defaultCartLineConfiguratorService: CartLineConfiguratorService = {
  patch: bffPatchCartLineConfiguration,
}

/** Looks up one line's persisted specification out of a
 * `CartViewWithLineSpecifications` — the sidecar array is sparse (only
 * configured lines have an entry, UID-434 WS-C), so `undefined` here is the
 * legitimate "never configured" reading, never a lookup failure. */
export function findCartLineSpecification(
  cart: CartViewWithLineSpecifications,
  sourceableId: string,
): CartLineSpecification | undefined {
  return cart.lineSpecifications?.find((entry) => entry.sourceableId === sourceableId)
    ?.specification
}

/**
 * Bug fix (reported via chat, cart-line-table Requirements column) — the row
 * was rendering ONLY the server's `CartLinePresentation.materialDescription`
 * (a catalog-line-oriented "brand · in stock" string) in the Requirements
 * cell, even though `GET /cart` already returns the line's FULL persisted
 * `CartLineSpecification` in `lineSpecifications` once a part has gone
 * through "Edit configuration" — none of process/material/finish/color/
 * tolerance/threaded-holes/inspections/certificates/additional-requirements
 * ever reached the UI for a configured line. This formats every populated
 * field of a `CartLineSpecification` into the same single-line, `·`-joined
 * style `formatRequirementsDetail` already uses for the Parts Library
 * table's Requirements cell (`../parts/format.ts`) — same rendering
 * convention, new call site. Returns `null` (render nothing) if the spec has
 * no populated fields, same "omit rather than render empty" contract as that
 * helper.
 */
export function formatCartLineSpecificationSummary(
  specification: CartLineSpecification | undefined,
  formatProcessLabel: (raw: string) => string,
  formatRequirementsDetail: (parts: readonly (string | null | undefined)[]) => string | null,
): string | null {
  if (!specification) return null
  const threadedHoles =
    specification.threadedHoleCount != null && specification.threadedHoleCount > 0
      ? `${specification.threadedHoleCount} threaded hole${specification.threadedHoleCount === 1 ? '' : 's'}`
      : null
  return formatRequirementsDetail([
    specification.process != null ? formatProcessLabel(specification.process) : null,
    specification.material,
    specification.finish,
    specification.color,
    specification.tolerance,
    threadedHoles,
    specification.inspections && specification.inspections.length > 0
      ? specification.inspections.join(', ')
      : null,
    specification.certificates && specification.certificates.length > 0
      ? specification.certificates.join(', ')
      : null,
    specification.additionalRequirements,
  ])
}
